import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';

const STATUS_VARIANT = { DRAFT: 'neutral', SUBMITTED: 'info', ACKNOWLEDGED: 'success' };
const stars = (r) => (r ? '★'.repeat(r) + '☆'.repeat(5 - r) : '-');

export function ReviewDetailModal({ reviewId, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const detail = useQuery({ queryKey: ['hr', 'review', reviewId], queryFn: () => hrApi.getReview(reviewId).then((r) => r.items), enabled: Boolean(reviewId) });
  const review = detail.data;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['hr', 'reviews'] });
    queryClient.invalidateQueries({ queryKey: ['hr', 'review', reviewId] });
  };
  const run = (fn) => ({ mutationFn: fn, onSuccess: () => { setError(null); invalidate(); }, onError: (e) => setError(e?.message || t('common.loadError')) });
  const submit = useMutation(run(() => hrApi.submitReview(reviewId)));
  const acknowledge = useMutation(run(() => hrApi.acknowledgeReview(reviewId)));
  const busy = submit.isPending || acknowledge.isPending;

  return (
    <Modal
      open={Boolean(reviewId)}
      onClose={onClose}
      title={review ? `${review.reviewNumber} · ${review.employeeName}` : t('rev.detail.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
          {review?.status === 'DRAFT' && <Button variant="primary" disabled={busy} onClick={() => submit.mutate()}>{t('rev.action.submit')}</Button>}
          {review?.status === 'SUBMITTED' && <Button variant="primary" disabled={busy} onClick={() => acknowledge.mutate()}>{t('rev.action.acknowledge')}</Button>}
        </>
      }
    >
      {review && (
        <div className="form-stack">
          <p className="muted text-sm" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge variant={STATUS_VARIANT[review.status]}>{t(`rev.status.${review.status}`)}</Badge>
            {t('rev.field.period')}: {review.period}
            <span className="mono">{stars(review.overallRating)}</span>
          </p>
          {review.strengths && <div><strong>{t('rev.field.strengths')}</strong><p>{review.strengths}</p></div>}
          {review.improvements && <div><strong>{t('rev.field.improvements')}</strong><p>{review.improvements}</p></div>}

          {(review.goals || []).length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>{t('rev.field.goalTitle')}</th><th className="num">{t('rev.col.rating')}</th></tr></thead>
                <tbody>
                  {review.goals.map((g) => (
                    <tr key={g.id}><td>{g.title}</td><td className="num mono">{stars(g.rating)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {error && <div className="alert danger">{error}</div>}
        </div>
      )}
    </Modal>
  );
}
